import React from "react";

import { tz } from "@date-fns/tz";
import { format, parseISO } from "date-fns";

import { Callout } from "src/design-system/Callout";
import { ModelOption } from "src/genai/ModelSelector/types";

type NudgeVariant = "info" | "warning";

export type LifecycleNudge = {
  variant: NudgeVariant;
  body: React.ReactNode;
  /** Where "Switch model" goes; absent means the callout can only be dismissed. */
  switchTarget?: ModelOption;
};

type UpgradeNudgeProps = {
  nudge: LifecycleNudge;
  /** The single call-to-action (e.g. "Switch model" when editable, else "Dismiss"). */
  action: { text: string; onClick: () => void };
};

const RETIREMENT_DATE_FORMAT = "d MMM yyyy";

const formatRetirementDate = (isoInstant: string): string =>
  format(parseISO(isoInstant), RETIREMENT_DATE_FORMAT, { in: tz("Etc/UTC") });

/**
 * Model names never wrap.
 */
const SelectedModelName: React.FC<{ name: string }> = ({ name }) => (
  <span className="whitespace-nowrap">{name}</span>
);

const ModelName: React.FC<{ name: string }> = ({ name }) => (
  <span className="whitespace-nowrap font-inter-medium-12px">{name}</span>
);

/** The underlying model id, stripped of any `:byok:<credential>` suffix. */
const baseModelId = (optionId: string): string => optionId.split(":byok:")[0];

/**
 * Resolve the model we recommend switching to. A BYOK selection only matches a
 * successor on the same credential (compound `model_id:byok:credential` id);
 * suggesting a shared deployment would move the workload off the customer's key.
 */
const resolveSuccessor = (
  models: ModelOption[],
  selected: ModelOption,
): ModelOption | undefined => {
  if (selected.successorId == null) return undefined;
  const optionId = selected.byok
    ? `${selected.successorId}:byok:${selected.id.split(":byok:")[1]}`
    : selected.successorId;
  return models.find((model) => model.id === optionId);
};

/**
 * Name the model that serves the selection's traffic. Rerouting keys on the
 * bare `model_id` and keeps the credential, so — unlike the switch target —
 * any option carrying that model names it. Falls back to the raw id, so an
 * announced reroute is never reported as "no longer serving".
 */
const resolveRerouteTargetName = (
  models: ModelOption[],
  selected: ModelOption,
): string | undefined => {
  const targetId = selected.rerouteTargetId;
  if (targetId == null) return undefined;
  const sameCredentialId = selected.byok
    ? `${targetId}:byok:${selected.id.split(":byok:")[1]}`
    : undefined;
  const named =
    (sameCredentialId != null
      ? models.find((model) => model.id === sameCredentialId)
      : undefined) ??
    models.find((model) => baseModelId(model.id) === targetId);
  return named?.displayName ?? targetId;
};

/**
 * Copy and severity for the selected model's lifecycle state: a deprecation
 * warns, a mere supersession informs, and anything else has nothing to say
 * (`undefined`).
 */
const nudgeContent = (
  model: ModelOption,
  successor?: ModelOption,
  rerouteTargetName?: string,
): { variant: NudgeVariant; body: React.ReactNode } | undefined => {
  const retirementDate = model.sunsetDate
    ? formatRetirementDate(model.sunsetDate)
    : undefined;

  const recommendation =
    successor && successor.displayName !== rerouteTargetName ? (
      <>
        {" "}
        We recommend switching to <ModelName name={successor.displayName} />.
      </>
    ) : null;

  if (model.lifecycle === "deprecated") {
    if (retirementDate && rerouteTargetName) {
      return {
        variant: "warning",
        body: (
          <>
            <SelectedModelName name={model.displayName} /> is deprecated.
            Traffic is automatically rerouted to{" "}
            <ModelName name={rerouteTargetName} /> from {retirementDate}.
            {recommendation}
          </>
        ),
      };
    }
    if (retirementDate) {
      return {
        variant: "warning",
        body: (
          <>
            <SelectedModelName name={model.displayName} /> is deprecated and
            serves no traffic from {retirementDate}.{recommendation}
          </>
        ),
      };
    }
    return {
      variant: "warning",
      body: successor ? (
        <>
          <SelectedModelName name={model.displayName} /> is deprecated. We
          recommend switching to <ModelName name={successor.displayName} />.
        </>
      ) : (
        <>
          <SelectedModelName name={model.displayName} /> is deprecated and may
          be retired by its provider.
        </>
      ),
    };
  }

  if (successor) {
    return {
      variant: "info",
      body: (
        <>
          <SelectedModelName name={model.displayName} /> is superseded by{" "}
          <ModelName name={successor.displayName} />.
        </>
      ),
    };
  }

  return undefined;
};

/**
 * Everything the picker needs to nudge about its selection: what to say, how
 * loudly, and where "Switch model" would go. `undefined` when the selection
 * warrants no callout at all.
 */
export const resolveNudge = ({
  models,
  offeredModels,
  selected,
  isReadOnly,
}: {
  /** The full catalog — the models we name may not be selectable. */
  models: ModelOption[];
  /** The subset the dropdown offers, i.e. what the user could pick instead. */
  offeredModels: ModelOption[];
  selected: ModelOption | undefined;
  isReadOnly: boolean;
}): LifecycleNudge | undefined => {
  if (selected == null) return undefined;

  const successor = resolveSuccessor(models, selected);
  const content = nudgeContent(
    selected,
    successor,
    resolveRerouteTargetName(models, selected),
  );
  if (content == null) return undefined;

  // Offer a switch only to a model the user could have picked themselves:
  // one the dropdown lists (so never a deprecated one) and not disabled.
  const switchTarget =
    !isReadOnly &&
    successor != null &&
    !successor.disabled &&
    offeredModels.some((model) => model.id === successor.id)
      ? successor
      : undefined;

  // A supersession is only worth interrupting for when there is somewhere to
  // go; a deprecation always warrants the callout, since the traffic moves
  // whether or not the user acts.
  if (
    selected.lifecycle !== "deprecated" &&
    switchTarget == null &&
    !isReadOnly
  ) {
    return undefined;
  }

  return { ...content, switchTarget };
};

/**
 * Inline callout shown below the trigger when the selected model is superseded
 * or deprecated by its provider.
 */
export const UpgradeNudge: React.FC<UpgradeNudgeProps> = ({
  nudge,
  action,
}) => (
  <div className="mt-2.5">
    <Callout action={action} dataLoc="upgrade-nudge" variant={nudge.variant}>
      {nudge.body}
    </Callout>
  </div>
);
